import type dayjs from 'dayjs';

/**
 * Calculates the start and end dates for the next week based on the current start date.
 * If the calculated end date is after today's date, it adjusts the end date to today.
 *
 * @param currentStartDate - The start date of the current week.
 * @param today - The current date (used to ensure the end date does not exceed today).
 * @returns A tuple containing the start date and end date of the next week.
 */
export const getNextWeekDates = (
  currentStartDate: dayjs.Dayjs,
  today: dayjs.Dayjs
): [startDate: dayjs.Dayjs, endDate: dayjs.Dayjs] => {
  const nextStartDate = currentStartDate.add(1, 'week').isAfter(today)
    ? currentStartDate
    : currentStartDate.add(1, 'week');
  const nextEndDate = nextStartDate.add(6, 'days').isAfter(today)
    ? today
    : nextStartDate.add(6, 'days').endOf('day'); // We avoid using endOf('week') to avoid issues with different locales
  return [nextStartDate, nextEndDate];
};

/**
 * Calculates the start and end dates for the previous week based on the current start date.
 *
 * @param currentStartDate - The start date of the current week.
 * @returns A tuple containing the start date and end date of the previous week.
 */
export const getPreviousWeekDates = (
  currentStartDate: dayjs.Dayjs
): [startDate: dayjs.Dayjs, endDate: dayjs.Dayjs] => {
  const previousStartDate = currentStartDate.subtract(1, 'week');
  const previousEndDate = previousStartDate.add(6, 'days').endOf('day'); // We avoid using endOf('week') to avoid issues with different locales
  return [previousStartDate, previousEndDate];
};
